import { useEffect, useMemo, useState } from "react";
import { Form, Col, Row } from "antd";
import useGet from "../../../global/hooks/useGet";
import usePost from "../../../global/hooks/usePost";
import usePut from "../../../global/hooks/usePut";
import useDelete from "../../../global/hooks/useDelete";
import CustomInput from "../../../global/components/inputWidgets/CustomInput";
import CustomSelect from "../../../global/components/inputWidgets/CustomSelect";
import { useNavigate, useParams } from "react-router-dom";
import {
  openConfirmNotification,
  openErrorNotification,
} from "../../../global/utils/toast";
import {
  CheckCircleOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  UnorderedListOutlined,
  InfoCircleOutlined,
  SettingOutlined,
  GiftFilled,
} from "@ant-design/icons";
import CommonBreadcrumb from "../../../global/components/CommonBreadcrumb";
import CommonButton from "../../../global/components/CommonButton";
import { getModifiedResponse } from "../../../global/utils/responseModifier";
import ConfirmModal from "../../../global/components/ConfirmModal";
import DeviceIcon from "../../../assets/images/vectors/SideBarIcons/DeviceIcon";
import AccountCreationModal from "../../../global/components/AccountCreationModal";
import OemIcon from "../../../assets/images/vectors/SideBarIcons/OemIcon";
import DealerIcon from "../../../assets/images/vectors/SideBarIcons/DealerIcon";
import AccountIcon from "../../../assets/images/vectors/SideBarIcons/AccountIcon";
import { macValidator } from "../../../global/utils/macValidator";
import { DeviceLogsOrdersPanel } from "./DeviceLogsOrdersPanel";
import { FaDollarSign } from "react-icons/fa";
import { hasRole } from "../../../global/hooks/getUserRole";
import {
  applyExpiredToOptions,
  modelIconMap,
  orderTypeOptions,
  productIconMap,
  siteTypeIconMap,
  getCreatedId,
  toVisibleOptionList,
  toDisplayDateTime,
  buildLicenseOptionsState,
} from "../../../utils/Common";
import DoorTypeIcon from "../../../assets/images/vectors/DoorTypeIcon";
import OptionCards from "../../../global/components/inputWidgets/OptionCards";
import { getUserRoles } from "../../../global/components/PrivateRoute";
import { useAuthData } from "../../../providers/AuthProvider";
import { PrePurchaseLicensePanel } from "../../MySite/Components/PrePurchaseLicenseForm";

export const DeviceForm = ({ type, prepurchasePage = false }) => {
  const { userData } = useAuthData();
  const { isSystemUser, isDealerUser } = getUserRoles(userData);

  const [showModal, setShowModal] = useState(false);
  const [OemNo, setOemNo] = useState(null);
  const [dealerNo, setDealerNo] = useState(null);
  const [form] = Form.useForm();
  const { id } = useParams();
  const navigate = useNavigate();
  const [sitetype, setSiteType] = useState(0);
  const [licenseType, setLicenseType] = useState(0);
  const [licenseOptionsData, setLicenseOptionsData] = useState({});

  const prepurchasedPostUrl = `${process.env.REACT_APP_API_URL}/api/devices/pre-purchased-items/`;
  const {
    loading: prepurchasedPostLoading,
    error: prepurchasedPostError,
    postData: prepurchasedPostAction,
  } = usePost(prepurchasedPostUrl);

  const [isOpenModal, setIsOpenModal] = useState(false);
  const [modalType, setModalType] = useState("");
  const [defaultData, setDefaultData] = useState({
    DealerNo: null,
    OemNo: null,
    CustomerNo: null,
  });

  const url = id
    ? `${process.env.REACT_APP_API_URL}/api/devices/${id}/`
    : `${process.env.REACT_APP_API_URL}/api/devices/`;
  const optionUrl = `${process.env.REACT_APP_API_URL}/api/devices/options/`;
  const { data, loading, refetch } = useGet(id ? url : null);

  const licenseOptionUrl = `${process.env.REACT_APP_API_URL}/api/licenses/options/`;
  const { data: licenseOptionData } = useGet(licenseOptionUrl);
  const modifiedOpLicense = useMemo(
    () => toVisibleOptionList(licenseOptionData?.data?.options),
    [licenseOptionData],
  );

  const codesUrl = `${process.env.REACT_APP_API_URL}/api/licensecodes/codes/?limit=100`;
  const { data: codesData, loading: codesDataLoading } = useGet(codesUrl);

  const { data: optionsData, refetch: optionDataRefetch } = useGet(optionUrl);

  const {
    data: postData,
    error: postError,
    postData: postAction,
    loading: postLoading,
  } = usePost(url);
  const {
    data: putData,
    error: putError,
    putData: putAction,
    loading: putLoading,
  } = usePut(url);
  const {
    data: deletedData,
    error: deletedDataError,
    deleteData,
  } = useDelete(url);

  const dealerOptionsUrl =
    OemNo || data?.data?.OemNo || defaultData?.OemNo
      ? `${process.env.REACT_APP_API_URL}/api/dealers/oems/${
          OemNo
            ? OemNo
            : defaultData?.OemNo
              ? defaultData.OemNo
              : data?.data?.OemNo
        }/dealers/`
      : null;

  const { data: dealerOptions, refetch: delaerOpRefetch } =
    useGet(dealerOptionsUrl);

  const custmerOptionsUrl =
    dealerNo || data?.data?.DealerNo || defaultData?.DealerNo
      ? `${process.env.REACT_APP_API_URL}/api/dealers/${
          dealerNo
            ? dealerNo
            : defaultData?.DealerNo
              ? defaultData.DealerNo
              : data?.data?.DealerNo
        }/customers/`
      : null;

  const { data: custmerOptions, refetch: customerDataRefetch } =
    useGet(custmerOptionsUrl);

  const selectedOemNo = OemNo || defaultData?.OemNo || data?.data?.OemNo;

  const { data: oemData, loading: oemLoading } = useGet(
    type === "info" && selectedOemNo
      ? `${process.env.REACT_APP_API_URL}/api/oems/${selectedOemNo}/`
      : null,
  );
  const oemInfo = oemData?.data ?? oemData;
  const isOemLicense = Number(oemInfo?.OemLicense) === 1;

  const { data: oemCodesData, loading: oemCodesLoading } = useGet(
    isOemLicense
      ? `${process.env.REACT_APP_API_URL}/api/licensecodes/codes/?limit=0&sort_by=Item&order=asc&OemNo=${selectedOemNo}`
      : null,
  );

  const allowedItemNos = useMemo(
    () =>
      isOemLicense && oemCodesData?.data
        ? new Set(oemCodesData.data.map((code) => Number(code.Item)))
        : null,
    [isOemLicense, oemCodesData],
  );

  const modifiedProducts = useMemo(
    () => getModifiedResponse(optionsData, "products"),
    [optionsData],
  );
  const modifiedProductOptions = useMemo(
    () =>
      modifiedProducts.map((product) => ({
        ...product,
        icon: productIconMap[product.value],
        searchLabel: product.label,
      })),
    [modifiedProducts],
  );
  const modifiedModels = useMemo(
    () => getModifiedResponse(optionsData, "models"),
    [optionsData],
  );
  const modifiedModelsOptions = useMemo(
    () =>
      modifiedModels.map((model) => ({
        ...model,
        icon: modelIconMap[model.value],
        searchLabel: model.label,
      })),
    [modifiedModels],
  );

  const modifiedOems = useMemo(
    () => getModifiedResponse(optionsData, "oems"),
    [optionsData],
  );
  const modifiedSiteTypes = useMemo(() => {
    const siteTypes = getModifiedResponse(optionsData, "sitetype");
    if (hasRole(7, 8, 9) && !["edit", "info"].includes(type)) {
      return siteTypes.filter((item) => [0, 3, 4].includes(item.value));
    }
    return siteTypes;
  }, [optionsData, type]);

  const modifiedSiteTypeOptions = useMemo(
    () =>
      modifiedSiteTypes.map((site) => ({
        ...site,
        icon: siteTypeIconMap[site.value],
        searchLabel: site.label,
      })),
    [modifiedSiteTypes],
  );
  const modifiedNodeTypes = useMemo(
    () => getModifiedResponse(optionsData, "nodetype"),
    [optionsData],
  );

  const modifiedDealers = useMemo(
    () => getModifiedResponse(dealerOptions, null),
    [dealerOptions],
  );

  const modifiedCustomers = useMemo(
    () => getModifiedResponse(custmerOptions, null),
    [custmerOptions],
  );

  const modifiedOp = useMemo(
    () => toVisibleOptionList(optionsData?.data?.options),
    [optionsData],
  );

  useEffect(() => {
    if (hasRole(7, 8, 9)) {
      setOemNo(userData?.oem_no);
      setDealerNo(userData?.dealer_no);
    }
  }, []);

  const onFinish = async (values) => {
    if (type === "info") return;
    try {
      const basicPayload = {
        DeviceName: values.DeviceName ?? "",
        DeviceDesc: values.DeviceDesc ?? "",
        OemNo: values.OemNo ?? (hasRole(7, 8, 9) ? userData?.oem_no : null),
        DealerNo:
          values.DealerNo ?? (hasRole(7, 8, 9) ? userData?.dealer_no : null),
        CustomerNo: values.CustomerNo ?? null,
        Mac: values.Mac ?? null,
        Product: Number(values.Product ?? 0),
      };

      if (form.getFieldValue("Expired")) {
        basicPayload.Options = applyExpiredToOptions(
          id ? data?.data?.Options : [],
          true,
        );
      }

      if (id) {
        await putAction(basicPayload);
      } else {
        await postAction(basicPayload);
      }
    } catch (error) {
      openErrorNotification("Something went wrong.");
      console.log(error);
    }
  };

  const onReset = () => {
    navigate(-1);
  };

  const onDeleteConfirm = async () => {
    try {
      await deleteData();
    } catch (error) {
      openErrorNotification("Something went wrong.");
    }
    setShowModal(false);
  };

  useEffect(() => {
    setLicenseType(orderTypeOptions[0].value);
    if (!id) {
      form.setFieldsValue({
        Model: modifiedModelsOptions[0]?.value,
        LicenseType: orderTypeOptions[0].value,
      });
    }
  }, [optionsData]);

  useEffect(() => {
    if (
      !id ||
      loading ||
      !codesData?.data ||
      !modifiedOp ||
      !modifiedModelsOptions?.length ||
      !modifiedOpLicense ||
      !modifiedSiteTypes?.length
    )
      return;

    const getDeviceInfoById = () => {
      try {
        form.resetFields();
        if (data?.data) {
          const currentSiteType = data.data.SiteType;
          let initialLicenseType;

          if ([0, 1, 2].includes(currentSiteType)) {
            initialLicenseType = orderTypeOptions[1].value; // lifetime
          } else if ([3, 4].includes(currentSiteType)) {
            initialLicenseType = orderTypeOptions[0].value; // Monthly
          } else {
            initialLicenseType = 0; // default
          }
          setLicenseType(initialLicenseType);
          form.setFieldsValue({
            DeviceName: data?.data?.DeviceName,
            DeviceDesc: data?.data?.DeviceDesc,
            DealerNo: data?.data?.DealerNo,
            CustomerNo: data?.data?.CustomerNo,
            SiteType: data?.data?.SiteType,
            Type: data?.data?.Type,
            NodeType: data?.data?.NodeType,
            Options: data.data.Options?.map((item) => String(item)),
            Mac: data?.data?.Mac,
            Product: data?.data?.Product,
            Model: data?.data?.Model,
            OemNo: data?.data?.OemNo,
            AddTime: toDisplayDateTime(data.data.AddTime),
            ActTime: toDisplayDateTime(data.data.ActTime),
            StartTime: toDisplayDateTime(data.data.StartTime),
            EndTime: toDisplayDateTime(data.data.EndTime),
            Channel: data?.data?.Channel,
            Lockset: data?.data?.Lockset,
            Facegate: data?.data?.Facegate,
            Subnode: data?.data?.Subnode,
            ContLock: data?.data?.ContLock,
            Intercom: data?.data?.Intercom,
            Sicucom:
              data.data.Sicucom != null ? Number(data.data.Sicucom) : null,
            Invitation: data.data.Invitation
              ? data.data.Invitation * 100
              : null,
            LicenseKey:
              data?.data?.LicenseKey !== null
                ? String(data?.data?.LicenseKey)
                : "",
            SiteKey:
              data?.data?.SiteKey !== null ? String(data?.data?.SiteKey) : "",
            FirstName: data?.data?.FirstName
              ? String(data?.data?.FirstName)
              : "",
            LastName: data?.data?.LastName ? String(data?.data?.LastName) : "",
            Company: data?.data?.Company ? String(data?.data?.Company) : "",
            Email: data?.data?.Email ? String(data?.data?.Email) : "",
            Phone: data?.data?.Phone ? String(data?.data?.Phone) : "",
            Address: data?.data?.Address ? String(data?.data?.Address) : "",
            Address2: data?.data?.Address2 ? String(data?.data?.Address2) : "",
            InstallType: data?.data?.InstallType
              ? String(data?.data?.InstallType)
              : "",
            Suggestion: data?.data?.Suggestion
              ? String(data?.data?.Suggestion)
              : "",
            ProxyNo: data?.data?.ProxyNo ? String(data?.data?.ProxyNo) : "",
            SecurePort: data?.data?.SecurePort
              ? String(data?.data?.SecurePort)
              : "",
            DebugPort: data?.data?.DebugPort
              ? String(data?.data?.DebugPort)
              : "",
            Timeout: data?.data?.Timeout ? String(data?.data?.Timeout) : "",
            Debug: data?.data?.Debug
              ? data?.data?.Debug === 0
                ? "No"
                : "Yes"
              : "",
            Version: data?.data?.Version ? String(data?.data?.Version) : "",
            Https: data?.data?.Https
              ? data?.data?.Https === 0
                ? "No"
                : "Yes"
              : "",
            Timezone: data?.data?.Timezone ? String(data?.data?.Timezone) : "",
            Online: data?.data?.Online
              ? data?.data?.Debug === 0
                ? "No"
                : "Yes"
              : "",
            LicenseType: initialLicenseType,
            Expired: data.data.Options?.includes(64) ?? false,
          });

          setDefaultData((prev) => ({
            ...prev,
            OemNo: data?.data?.OemNo || null,
            DealerNo: data?.data?.DealerNo || null,
            CustomerNo: data?.data?.CustomerNo || null,
          }));

          setLicenseOptionsData(
            buildLicenseOptionsState(data.data, modifiedOpLicense, modifiedOp),
          );
          setSiteType(data.data.SiteType || 0);
        } else {
          console.warn("No data found for the given ID");
        }
      } catch (error) {
        console.log({ error });
        openErrorNotification("Error fetching data!");
      }
    };

    getDeviceInfoById();
  }, [
    id,
    data,
    loading,
    codesData,
    modifiedOp,
    modifiedModelsOptions,
    modifiedOpLicense,
    modifiedSiteTypes,
  ]);

  useEffect(() => {
    if (postError) {
      openErrorNotification(
        postError?.response?.data?.message || "Something went wrong",
      );
    } else if (!postError && postData) {
      openConfirmNotification("Success");
      const newId = getCreatedId(postData);
      if (newId != null) {
        navigate(`/device/info/${newId}`);
      } else {
        navigate(-1);
      }
    }
  }, [postError, postData]);
  useEffect(() => {
    if (putError) {
      openErrorNotification(
        putError?.response?.data?.message || "Something went wrong",
      );
    } else if (!putError && putData) {
      refetch();
      openConfirmNotification("Success");
      navigate(-1);
    }
  }, [putData, putError]);

  useEffect(() => {
    if (prepurchasedPostError) {
      openErrorNotification(
        prepurchasedPostError?.response?.data?.message ||
          "Something went wrong",
      );
    }
  }, [prepurchasedPostError]);

  useEffect(() => {
    if (deletedData && !deletedDataError) {
      openConfirmNotification("Success");
      navigate("/device");
    } else if (deletedDataError) {
      openErrorNotification(
        deletedDataError?.response?.data?.message || "Something went wrong",
      );
    }
  }, [deletedData, deletedDataError]);

  useEffect(() => {
    if (!id) {
      form.setFieldsValue({
        Product: modifiedProductOptions[0]?.value,
      });
    }
  }, [optionsData]);

  const handleCreateNew = (type) => {
    setModalType(type);
    setIsOpenModal(true);
  };

  useEffect(() => {
    optionDataRefetch();
    delaerOpRefetch();
    customerDataRefetch();
    if (
      defaultData?.OemNo ||
      defaultData?.DealerNo ||
      defaultData?.CustomerNo
    ) {
      form.setFieldsValue(defaultData);
    }
  }, [defaultData]);

  const prepurchaseDealerNo = data?.data?.DealerNo ?? userData?.dealer_no;

  const onApplyPrepurchased = async (rows, getSchedule) => {
    if (!id) {
      openErrorNotification("Device is not available.");
      return;
    }
    const result = await prepurchasedPostAction({
      dealer_no: Number(prepurchaseDealerNo),
      items: rows.map((row) => {
        const item = {
          purchase_no: row.PurchaseNo,
          device_no: Number(id),
          ...getSchedule(row),
        };
        if (row.ItemNo != null && row.ItemNo !== "") item.item_no = row.ItemNo;
        return item;
      }),
    });
    if (result == null) return;
    openConfirmNotification("Success");
    refetch();
    navigate(`/device/info/${id}`);
  };

  const breadcrumbItems = [
    {
      label: "Device",
      href: "/device",
    },
    {
      label: id ? (type === "info" ? "Information" : "Edit") : "Add",
      href: id
        ? type === "info"
          ? `/device/info/${id}`
          : `/device/edit/${id}`
        : "/device/add",
    },
  ];

  const getActionButtons = (showDelete = false, showEdit = true) => {
    const buttons = showEdit
      ? [
          {
            label: "Edit",
            icon: <EditOutlined />,
            onClick: () => navigate(`/device/edit/${id}`),
          },
        ]
      : [];

    if (showDelete) {
      buttons.push({
        label: "Delete",
        icon: <DeleteOutlined />,
        onClick: () => setShowModal(true),
      });
    }

    buttons.push({
      label: "List",
      icon: <UnorderedListOutlined />,
      onClick: () => navigate("/device"),
    });

    return buttons;
  };

  const getExpiredToggle = (disabled) => ({
    name: "Expired",
    label: "Expired",
    toggle: true,
    ...(disabled ? { disabled: true } : { onClick: () => {} }),
  });

  const getEditButtons = () => [
    ...(id ? [getExpiredToggle(false)] : []),
    {
      label: "Apply",
      icon: <CheckCircleOutlined />,
      onClick: () => form.submit(),
      loading: postLoading || putLoading,
    },
    {
      label: "Cancel",
      icon: <CloseOutlined />,
      onClick: onReset,
    },
  ];

  const getPurchaseLicenseButtons = () => {
    if (!hasRole(1, 2, 7, 8)) return [];
    const deviceNo = data?.data?.DeviceNo ?? (id ? Number(id) : null);
    const dealerNoValue =
      data?.data?.DealerNo ?? (isDealerUser ? userData?.dealer_no : null);
    const oemNoValue = data?.data?.OemNo ?? userData?.oem_no;
    if (deviceNo == null || dealerNoValue == null) return [];

    return [
      {
        label: "Purchase License",
        icon: <FaDollarSign />,
        onClick: () => {
          if (oemNoValue == null || oemNoValue === "") {
            openErrorNotification("OEM is required.");
            return;
          }
          navigate(`/device/${deviceNo}/purchase-license`);
        },
      },
    ];
  };

  const getButtonsData = () => {
    const roleValue = userData?.role_value;
    const isRoleGroup1 = [1, 2].includes(roleValue);
    const isRoleGroup2 = [4, 5].includes(roleValue);
    const isRoleGroup3 = [3, 6, 9].includes(roleValue);
    const isRoleGroup4 = [7, 8].includes(roleValue);

    if (type !== "info") {
      return getEditButtons();
    }

    if (isRoleGroup1) {
      return [
        ...getPurchaseLicenseButtons(),
        getExpiredToggle(true),
        ...getActionButtons(true),
      ];
    }
    if (isRoleGroup3) {
      return [getExpiredToggle(true), ...getActionButtons(false, false)];
    }
    if (isRoleGroup2) {
      return [
        ...getPurchaseLicenseButtons(),
        getExpiredToggle(true),
        ...getActionButtons(false, false),
      ];
    }
    if (isRoleGroup4) {
      const prepurchasedBtn = [0, 1, 2].includes(data?.data?.SiteType)
        ? [
            {
              label: "Pre-Purchased License",
              icon: <GiftFilled />,
              onClick: () => navigate(`/device/${id}/pre-purchase-license`),
            },
          ]
        : [];
      return [
        ...prepurchasedBtn,
        ...getPurchaseLicenseButtons(),
        getExpiredToggle(true),
        ...getActionButtons(false, true),
      ];
    }

    return getActionButtons(false, false);
  };

  const buttonsData = getButtonsData();

  useEffect(() => {
    if (
      id &&
      type === "edit" &&
      [3, 6, 10, 11, 12].includes(userData?.role_value)
    ) {
      navigate("/");
    }
  }, []);
  useEffect(() => {
    const currentSiteType = form.getFieldValue("SiteType");

    if (currentSiteType === undefined) return;
    if (id && loading) return;
    let newLicenseType;

    if ([0, 1, 2].includes(currentSiteType)) {
      newLicenseType = orderTypeOptions[1].value; // lifetime
    } else if ([3, 4].includes(currentSiteType)) {
      newLicenseType = orderTypeOptions[0].value; // Monthly
    }

    if (newLicenseType === undefined) return;

    // Update regardless of previous value to handle page transitions
    setLicenseType(newLicenseType);
    form.setFieldsValue({ LicenseType: newLicenseType });
  }, [
    sitetype,
    codesData,
    modifiedModelsOptions,
    modifiedSiteTypes,
    type,
    data,
  ]);

  return (
    <div>
      <AccountCreationModal
        isOpenModal={isOpenModal}
        modalType={modalType}
        setIsOpenModal={setIsOpenModal}
        setDefaultData={setDefaultData}
        defaultData={defaultData}
        setModalType={setModalType}
        isModal={true}
        isModalForm={true}
        title={
          <div className="flex items-center bg-gray-200 p-2 rounded-md">
            {modalType === "OEM" ? (
              <OemIcon className="text-[15px] text-black ml-1" />
            ) : modalType === "Dealer" ? (
              <DealerIcon className="text-[15px] text-black ml-1" />
            ) : modalType === "Customer" ? (
              <AccountIcon className="text-[15px] text-black ml-1" />
            ) : null}
            <span className="text-[14px] font-[Poppins] text-black  ml-2">
              {isOpenModal ? `Add ${modalType}` : "Send Invitation"}
            </span>
          </div>
        }
      />
      {prepurchasePage && (
        <PrePurchaseLicensePanel
          breadcrumbItems={[
            { label: "Device", href: "/device" },
            { label: "Information", href: `/device/info/${id}` },
            {
              label: "Pre-Purchased License",
              href: `/device/${id}/pre-purchase-license`,
            },
          ]}
          cancelPath={`/device/info/${id}`}
          dealerNo={prepurchaseDealerNo}
          loading={loading}
          applying={prepurchasedPostLoading}
          onApply={onApplyPrepurchased}
        />
      )}
      <div className={prepurchasePage ? "hidden" : ""}>
        <Form
          validateTrigger={"onChange"}
          form={form}
          onFinish={onFinish}
          onFinishFailed={({ errorFields }) => {
            const firstError = errorFields?.[0]?.errors?.[0];
            if (firstError) openErrorNotification(firstError);
          }}
          scrollToFirstError
          layout={"vertical"}
          autoComplete={"off"}
        >
          <div className="pb-4 sticky -mt-4 pt-[6px] top-[55px] z-10 bg-[#f4f5f9]">
            <CommonBreadcrumb breadcrumbItems={breadcrumbItems}>
              <CommonButton buttonsData={buttonsData} />
            </CommonBreadcrumb>
          </div>
          <div className="overflow-hidden border bg-white rounded-[4px]">
            <div className="flex items-center bg-gray-200 p-2.5">
              <DeviceIcon className="text-[18px] text-black " />
              <span className="text-[.935rem] text-black font-['Poppins'] ml-2">
                Device
              </span>
            </div>
            <div className="px-2 pt-1 pb-3">
              <Row gutter={[16, 8]}>
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                  <CustomInput
                    label="Device Name"
                    name="DeviceName"
                    disable={type === "info"}
                    loading={loading}
                  />
                </Col>
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                  <CustomInput
                    label="Description"
                    name="DeviceDesc"
                    disable={type === "info"}
                    loading={loading}
                  />
                </Col>
                {[1, 2, 3].includes(userData?.role_value) && (
                  <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                    <CustomSelect
                      label="OEM"
                      name="OemNo"
                      options={modifiedOems}
                      placeholder="Please select"
                      disabled={type === "info"}
                      required
                      onChange={(value) => {
                        setDefaultData((prev) => ({
                          ...prev,
                          OemNo: value,
                          DealerNo: null,
                          CustomerNo: null,
                        }));
                        setOemNo(value);
                        setDealerNo(null);
                        if (hasRole(7, 8, 9)) {
                          setSiteType(data?.data?.SiteType);
                        } else {
                          setSiteType(0);
                        }
                        form.setFieldsValue({
                          DealerNo: null,
                          CustomerNo: null,
                          SiteType: hasRole(7, 8, 9) ? data?.data?.SiteType : 0,
                        });
                      }}
                      handleCreateNew={handleCreateNew}
                      type={"OEM"}
                      allowClear={false}
                      loading={loading}
                    />
                  </Col>
                )}
                {[1, 2, 3, 4, 5, 6].includes(userData?.role_value) && (
                  <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                    <CustomSelect
                      label="Dealer"
                      name="DealerNo"
                      options={modifiedDealers}
                      placeholder="Please select"
                      disabled={type === "info" || !defaultData?.OemNo}
                      onChange={(value) => {
                        setDefaultData((prev) => ({
                          ...prev,
                          DealerNo: value,
                          CustomerNo: null,
                        }));
                        setDealerNo(value);
                        form.setFieldsValue({
                          CustomerNo: null,
                        });
                        if (!value) {
                          form.setFieldsValue({
                            SiteType: hasRole(7, 8, 9)
                              ? data?.data?.SiteType
                              : 0,
                          });
                        }
                      }}
                      handleCreateNew={handleCreateNew}
                      type={"Dealer"}
                      loading={loading}
                    />
                  </Col>
                )}
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].includes(userData?.role_value) && (
                  <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                    <CustomSelect
                      label="Customer"
                      name="CustomerNo"
                      options={modifiedCustomers}
                      placeholder="Please select"
                      disabled={
                        type === "info" ||
                        (hasRole(7, 8, 9)
                          ? !OemNo && !dealerNo
                          : !defaultData?.DealerNo)
                      }
                      handleCreateNew={handleCreateNew}
                      type={"Customer"}
                      loading={loading}
                    />
                  </Col>
                )}
                {type === "info" && (
                  <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                    <CustomSelect
                      label="Node Type"
                      name="NodeType"
                      options={modifiedNodeTypes}
                      placeholder="Please select"
                      disabled
                      loading={loading}
                    />
                  </Col>
                )}
                {type === "info" && (
                  <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                    <CustomSelect
                      label="Site Type"
                      name="SiteType"
                      options={
                        form.getFieldValue("SiteType") === 3
                          ? modifiedSiteTypeOptions?.filter(
                              (item) => item.value === 3,
                            )
                          : form.getFieldValue("SiteType") === 4
                            ? modifiedSiteTypeOptions?.filter(
                                (item) => item.value === 4,
                              )
                            : modifiedSiteTypeOptions?.filter(
                                (item) => ![3, 4].includes(item.value),
                              )
                      }
                      placeholder="Please select"
                      disabled
                      loading={loading}
                      allowClear={false}
                      showIcons
                    />
                  </Col>
                )}
                {(type !== "info" ||
                  (isSystemUser && [0, 1, 2].includes(sitetype))) && (
                  <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                    <CustomSelect
                      label="Product"
                      name="Product"
                      options={modifiedProductOptions}
                      placeholder="Please select"
                      disabled={type === "info"}
                      required
                      allowClear={!type}
                      loading={loading}
                      showIcons
                    />
                  </Col>
                )}
                {(type !== "info" ||
                  (isSystemUser && [0, 1, 2].includes(sitetype))) && (
                  <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                    <CustomInput
                      label="MAC"
                      name="Mac"
                      disable={id || type === "info"}
                      required={[0, 1, 2].includes(sitetype)}
                      rules={[{ validator: macValidator }]}
                      loading={loading}
                    />
                  </Col>
                )}
                {type === "info" && data?.data?.LicenseKey && (
                  <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                    <CustomInput
                      label="License Key"
                      name="LicenseKey"
                      disabled={type === "info"}
                      loading={loading}
                    />
                  </Col>
                )}
                {type === "info" && isSystemUser && (
                  <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                    <CustomInput
                      label="Add Time"
                      name="AddTime"
                      disabled
                      loading={loading}
                    />
                  </Col>
                )}
                {type === "info" && isSystemUser && (
                  <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                    <CustomInput
                      label="Activate Time"
                      name="ActTime"
                      disabled
                      loading={loading}
                    />
                  </Col>
                )}
              </Row>
            </div>
          </div>
          {type === "info" && !loading && (
            <div>
              <div className="mt-4 border bg-white rounded-[4px] overflow-hidden">
                <div className="flex items-center bg-gray-200 p-2.5">
                  <InfoCircleOutlined className="text-[18px] text-black " />
                  <span className="text-[.935rem] text-black font-['Poppins'] ml-2">
                    License Information
                  </span>
                </div>
                <div className="px-2 pt-1 pb-3">
                  <Row gutter={[16, 8]}>
                    <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                      <CustomSelect
                        label="Model"
                        name="Model"
                        options={modifiedModelsOptions}
                        placeholder="Please select"
                        disabled={type === "info"}
                        required
                        loading={loading}
                        showIcons
                      />
                    </Col>
                    <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                      <CustomInput
                        label="Door"
                        name="Type"
                        disabled={type === "info"}
                        type="number"
                        integerOnly
                        required
                        loading={loading}
                        prefix={<DoorTypeIcon />}
                        rules={[
                          {
                            required: true,
                            message: "Door is required",
                          },
                          {
                            validator: (_, value) => {
                              if (value && value > 65535) {
                                return Promise.reject(
                                  new Error(
                                    "Door must be less than or equal to 65535!",
                                  ),
                                );
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                      />
                    </Col>
                  </Row>
                </div>
              </div>
              <div className="mt-4 border bg-white rounded-[4px] overflow-hidden">
                <div className="flex items-center bg-gray-200 p-2.5">
                  <SettingOutlined className="text-[18px] text-black " />
                  <span className="text-[.935rem] text-black font-['Poppins'] ml-2">
                    License Options
                  </span>
                </div>
                <div className="p-3">
                  <OptionCards
                    name="Options"
                    options={modifiedOp}
                    disabled={type === "info"}
                    loading={
                      loading ||
                      codesDataLoading ||
                      oemLoading ||
                      (isOemLicense && oemCodesLoading)
                    }
                    codesData={codesData}
                    type={licenseType}
                    value={licenseOptionsData}
                    onChange={setLicenseOptionsData}
                    hidePrices
                    allowedItemNos={allowedItemNos}
                    emptyText={
                      isOemLicense
                        ? "No license options available for this OEM"
                        : undefined
                    }
                    oemNo={selectedOemNo}
                  />
                </div>
              </div>
            </div>
          )}
          {type === "info" && data?.data?.Guest ? (
            <div className="mt-4 bg-white rounded-[4px] overflow-hidden">
              <div className="flex items-center bg-gray-200 p-2.5 ">
                <InfoCircleOutlined className="text-[18px] text-black " />
                <span className="text-[.935rem] text-black font-['Poppins'] ml-2">
                  Customer Information
                </span>
              </div>
              <div className="px-2 pt-1 pb-3">
                <Row gutter={[16, 8]}>
                  <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                    <CustomInput
                      label="First Name"
                      name="FirstName"
                      disabled={type === "info"}
                      loading={loading}
                    />
                  </Col>
                  <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                    <CustomInput
                      label="Last Name"
                      name="LastName"
                      disabled={type === "info"}
                      loading={loading}
                    />
                  </Col>
                </Row>
                <Row gutter={[16, 8]}>
                  <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                    <CustomInput
                      label="Installing Company"
                      name="Company"
                      disabled={type === "info"}
                      loading={loading}
                    />
                  </Col>
                  <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                    <CustomInput
                      label="Email"
                      name="Email"
                      disabled={type === "info"}
                      loading={loading}
                    />
                  </Col>
                </Row>
                <Row gutter={[16, 8]}>
                  <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                    <CustomInput
                      label="Phone"
                      name="Phone"
                      disabled={type === "info"}
                      loading={loading}
                    />
                  </Col>
                  <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                    <CustomInput
                      label="Address"
                      name="Address2"
                      disabled={type === "info"}
                      loading={loading}
                    />
                  </Col>
                </Row>
                <Row gutter={[16, 8]}>
                  <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                    <CustomInput
                      label="Install Type"
                      name="InstallType"
                      disabled={type === "info"}
                      loading={loading}
                    />
                  </Col>
                  <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                    <CustomInput
                      label="Suggestion"
                      name="Suggestion"
                      disabled={type === "info"}
                      loading={loading}
                    />
                  </Col>
                </Row>
              </div>
            </div>
          ) : null}
        </Form>
        {type === "info" &&
          (isSystemUser || hasRole(7, 8)) &&
          !prepurchasePage && (
            <DeviceLogsOrdersPanel
              deviceNo={id}
              currentOrderOnly={!isSystemUser}
            />
          )}
      </div>

      <ConfirmModal
        showModal={showModal}
        setShowModal={setShowModal}
        onDeleteConfirm={onDeleteConfirm}
      />
    </div>
  );
};
